import React, { useState, useEffect, useCallback } from 'react';
import './BackgroundImageCarousel.css';

const BackgroundImageCarousel = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    
    // Generate image paths
    const imagePaths = Array.from({ length: 17 }, (_, i) => `/backgrounds/${i + 1}.JPG`);

    const totalImages = imagePaths.length;

    // Navigate to next image
    const nextImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }, [totalImages]);

    // Navigate to previous image
    const prevImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }, [totalImages]);

    // Auto-cycle effect
    useEffect(() => {
        if (!isAutoPlaying) return;
        
        const interval = setInterval(nextImage, 4000); // Change every 4 seconds
        return () => clearInterval(interval);
    }, [isAutoPlaying, nextImage]);

    // Keyboard navigation
    useEffect(() => {
        // Define interface for keyboard event handler
        
        const handleKeyDown = (e: React.KeyboardEvent | KeyboardEvent): void => {
            if (e.key === 'ArrowLeft') {
                setIsAutoPlaying(false);
                prevImage();
            } else if (e.key === 'ArrowRight') {
                setIsAutoPlaying(false);
                nextImage();
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [nextImage, prevImage]);

    // Handle click/touch navigation
    useEffect(() => {
        const handleAreaClick = (e: MouseEvent) => {
            const rect = (e.target as Element).getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const containerWidth = rect.width;
            
            // Left area is 30% of screen width, right area is 70%
            const leftAreaWidth = containerWidth * 0.3;
            
            setIsAutoPlaying(false);
            
            if (clickX < leftAreaWidth) {
                prevImage();
            } else {
                nextImage();
            }
        };
        
        window.addEventListener('click', handleAreaClick);
        
        return () => {
            window.removeEventListener('click', handleAreaClick);
        };
    }, [nextImage, prevImage]);

    // Resume auto-play after 3 seconds of inactivity
    useEffect(() => {
        if (isAutoPlaying) return;
        
        const timeout = setTimeout(() => {
            setIsAutoPlaying(true);
        }, 3000);
        
        return () => clearTimeout(timeout);
    }, [isAutoPlaying, currentImageIndex]);

    return (
        <div className="background-carousel">
        {/* Background Image */}
        <div 
        className="background"
        style={{
            '--background': `url(${imagePaths[currentImageIndex]})`,
        } as React.CSSProperties}
        />
        
        {/* Image Counter */}
        <div className="background-counter">
        {currentImageIndex + 1} / {totalImages}
        </div>
        
        {/* Preload next images for smooth transitions */}
        {imagePaths.map((path, index) => (
            <link
            key={index}
            rel="preload"
            as="image"
            href={path}
            style={{ display: 'none' }}
            />
        ))}
        </div>
    );
};

export default BackgroundImageCarousel;